/* Hematite Band Quiz controller.
 *
 * Drives the single-screen-at-a-time quiz funnel rendered by
 * sections/hematite-quiz.liquid. Responsibilities:
 *   - Screen navigation (intro -> q1..q8 -> interlude -> q9..q16 -> reveal)
 *   - localStorage persistence (resumable across refreshes)
 *   - Single vs multi-select option handling + validation
 *   - Auto-advance on single-select after a short pause
 *   - Progress bar fill (cold-ash -> ember as user progresses)
 *   - Match % computation at the interlude
 *   - Pack recommendation logic at the reveal:
 *       default = Shieldwall (3 bands, variants[2], Buy 2 Get 1 Free)
 *       upgrade to Longship (5 bands, variants[3], Buy 2 Get 3 Free) if
 *       ANY "strong" upsell signal triggered:
 *         Q2  = "Daily, all-day wear"
 *         Q11 = "Monthly or more" gift-giving
 *         Q12 = "3+ men in family / multi-adult household"
 *         Q13 = "3-6 months of gifts" OR "whole gift season"
 *         Q16 = "Very proactive" wellness
 *   - Falling-band overlay animation at interlude entry + reveal entry
 *   - Reveal CTA URL: /discount/IRON10?redirect=/products/hematite-band
 *
 * No external dependencies. Vanilla JS, plays nice with Shopify defer load.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-hematite-quiz]');
  if (!root) return;

  var STORAGE_KEY = 'hematite_quiz_state';
  var TOTAL_QUESTIONS = 16;
  var INTERLUDE_AFTER = 8;
  var PRODUCT_URL = root.getAttribute('data-product-url') || '/products/hematite-band';
  var CORNER_ASSET = root.getAttribute('data-corner-asset') || '';
  var DISCOUNT_CODE = root.getAttribute('data-discount-code') || 'IRON10';
  var VARIANT_3PACK = root.getAttribute('data-variant-3pack') || '';
  var VARIANT_5PACK = root.getAttribute('data-variant-5pack') || '';

  /* ---------- state ---------- */
  function loadState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { answers: {}, currentScreen: 'intro' };
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return { answers: {}, currentScreen: 'intro' };
      if (!parsed.answers) parsed.answers = {};
      if (!parsed.currentScreen) parsed.currentScreen = 'intro';
      return parsed;
    } catch (e) {
      return { answers: {}, currentScreen: 'intro' };
    }
  }

  function saveState() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) {}
  }

  function resetState() {
    state = { answers: {}, currentScreen: 'intro' };
    saveState();
  }

  var state = loadState();

  /* ---------- screens ---------- */
  var screens = Array.prototype.slice.call(root.querySelectorAll('.quiz-screen'));
  var screenById = {};
  screens.forEach(function (el) { screenById[el.getAttribute('data-screen')] = el; });

  var screenOrder = ['intro'];
  for (var i = 1; i <= TOTAL_QUESTIONS; i++) {
    screenOrder.push('q' + i);
    if (i === INTERLUDE_AFTER) screenOrder.push('interlude');
  }
  screenOrder.push('reveal');

  function indexOfScreen(id) { return screenOrder.indexOf(id); }

  function showScreen(id) {
    screens.forEach(function (el) {
      if (el.getAttribute('data-screen') === id) {
        el.classList.add('is-active');
      } else {
        el.classList.remove('is-active');
      }
    });
    state.currentScreen = id;
    saveState();

    /* Hide the progress bar on intro; show it for everything else
       except the reveal (the reveal screen has its own attention focus). */
    var pb = root.querySelector('[data-quiz-progress]');
    if (pb) {
      if (id === 'intro' || id === 'reveal') {
        pb.setAttribute('hidden', '');
      } else {
        pb.removeAttribute('hidden');
      }
    }
    updateProgress(id);

    /* Trigger the falling-sticks animation when entering the interlude
       or the reveal. The user explicitly wanted both touchpoints to
       feel like winning a prize. */
    if (id === 'interlude') {
      computeAndShowMatch();
      triggerFallingSticks(12);
      startAutoAdvance(screenById[id]);
    } else {
      stopAutoAdvance();
    }
    if (id === 'reveal') {
      computeAndShowReveal();
      triggerFallingSticks(16);
      startRevealRotator();
    } else {
      stopRevealRotator();
    }

    /* Scroll to top of the active screen so users always land at the
       question prompt, not somewhere mid-card. */
    if (root.scrollIntoView) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    /* Restore visual state of options on the question we just landed on
       (e.g. after refresh, mark previously-selected option as active). */
    syncOptionsFromState(id);
  }

  /* ---------- progress bar ---------- */
  function updateProgress(id) {
    var fill = root.querySelector('[data-quiz-progress-fill]');
    var label = root.querySelector('[data-quiz-progress-label]');
    if (!fill || !label) return;

    var qIndex = 0;
    if (/^q(\d+)$/.test(id)) qIndex = parseInt(id.replace('q', ''), 10);
    else if (id === 'interlude') qIndex = INTERLUDE_AFTER;
    else if (id === 'reveal') qIndex = TOTAL_QUESTIONS;

    var pct = Math.max(0, Math.min(100, (qIndex / TOTAL_QUESTIONS) * 100));
    fill.style.width = pct + '%';
    /* Label is the same numeric percentage as the bar fill so the bar
       and the text always agree. Rounded to a whole number. Suppressed
       on the intro (no progress yet) so the first screen reads clean. */
    if (qIndex > 0) {
      label.textContent = Math.round(pct) + '%';
    } else {
      label.textContent = '';
    }
  }

  /* ---------- options ---------- */
  function syncOptionsFromState(id) {
    var screen = screenById[id];
    if (!screen) return;
    var key = screen.getAttribute('data-question-key');
    if (!key) return;
    var saved = state.answers[key];
    var opts = screen.querySelectorAll('.quiz-option');
    Array.prototype.forEach.call(opts, function (btn) {
      btn.classList.remove('is-selected');
      if (saved == null) return;
      var val = btn.getAttribute('data-value');
      if (Array.isArray(saved)) {
        if (saved.indexOf(val) !== -1) btn.classList.add('is-selected');
      } else if (saved === val) {
        btn.classList.add('is-selected');
      }
    });
  }

  function handleOptionClick(e) {
    var btn = e.target.closest('.quiz-option');
    if (!btn) return;
    var screen = btn.closest('.quiz-screen');
    if (!screen) return;
    var key = screen.getAttribute('data-question-key');
    var multi = screen.getAttribute('data-question-multi') === 'true';
    var val = btn.getAttribute('data-value');
    if (!key) return;

    if (multi) {
      var current = Array.isArray(state.answers[key]) ? state.answers[key].slice() : [];
      var idx = current.indexOf(val);
      if (idx === -1) current.push(val);
      else current.splice(idx, 1);
      state.answers[key] = current;
      saveState();
      syncOptionsFromState(screen.getAttribute('data-screen'));
      ensureMultiNext(screen);
    } else {
      state.answers[key] = val;
      saveState();
      syncOptionsFromState(screen.getAttribute('data-screen'));
      /* Single-select questions auto-advance after a brief highlight pause
         so the user sees their choice register before moving on. */
      setTimeout(function () { advance(); }, 320);
    }
  }

  /* For multi-select questions we add a "Continue" button at the bottom
     once the user picks at least one option. Single-select doesn't need
     this because it auto-advances. */
  function ensureMultiNext(screen) {
    var key = screen.getAttribute('data-question-key');
    var current = state.answers[key];
    var hasSelection = Array.isArray(current) && current.length > 0;
    var existing = screen.querySelector('[data-quiz-multi-next]');
    if (hasSelection && !existing) {
      var wrap = document.createElement('div');
      wrap.className = 'quiz-screen__cta-row';
      wrap.setAttribute('data-quiz-multi-next', '');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'quiz-cta quiz-cta--primary';
      btn.innerHTML = 'CONTINUE <span aria-hidden="true">&rarr;</span>';
      btn.addEventListener('click', function () { advance(); });
      wrap.appendChild(btn);
      screen.appendChild(wrap);
    } else if (!hasSelection && existing) {
      existing.parentNode.removeChild(existing);
    }
  }

  /* ---------- navigation ---------- */
  function advance() {
    var cur = state.currentScreen;
    var idx = indexOfScreen(cur);
    if (idx === -1 || idx >= screenOrder.length - 1) return;
    var next = screenOrder[idx + 1];
    showScreen(next);
  }

  function handleAction(e) {
    var el = e.target.closest('[data-quiz-action]');
    if (!el) return;
    var action = el.getAttribute('data-quiz-action');
    if (action === 'start') {
      /* If user is resuming a previous session past q1, jump to last screen.
         Otherwise start fresh at q1. */
      var resume = state.currentScreen;
      if (resume && resume !== 'intro' && indexOfScreen(resume) > 0) {
        showScreen(resume);
      } else {
        showScreen('q1');
      }
    } else if (action === 'next') {
      advance();
    } else if (action === 'restart') {
      resetState();
      stopRevealRotator();
      showScreen('intro');
    }
  }

  /* ---------- match % at interlude ---------- */
  function computeAndShowMatch() {
    /* Base 85%. Add small bump per upsell-positive answer (capped at 95%).
       Real value is psychological, not statistical , but tying it to
       actual answers makes the screen feel responsive. */
    var upsellCount = countUpsellSignals();
    var pct = 85 + Math.min(10, upsellCount * 2);
    if (pct > 95) pct = 95;
    if (pct < 82) pct = 82;

    var smallNodes = root.querySelectorAll('[data-quiz-match-percent]');
    Array.prototype.forEach.call(smallNodes, function (n) { n.textContent = pct + '%'; });
    var bigNode = root.querySelector('[data-quiz-match-percent-big]');
    if (bigNode) bigNode.textContent = pct + '%';

    /* Animate the ring fill via SVG dasharray. circumference for r=52 ~= 326.7 */
    var ring = root.querySelector('[data-quiz-match-ring]');
    if (ring) {
      var circ = 2 * Math.PI * 52;
      ring.style.strokeDasharray = circ;
      ring.style.strokeDashoffset = circ * (1 - pct / 100);
    }
  }

  /* ---------- reveal logic ---------- */
  function countUpsellSignals() {
    /* Count answers that carry data-upsell. Multi-select answers count
       each upsell-flagged value individually. */
    var count = 0;
    screens.forEach(function (screen) {
      var key = screen.getAttribute('data-question-key');
      if (!key) return;
      var ans = state.answers[key];
      if (ans == null) return;
      var values = Array.isArray(ans) ? ans : [ans];
      values.forEach(function (v) {
        var opt = screen.querySelector('.quiz-option[data-value="' + cssEscape(v) + '"]');
        if (opt && opt.getAttribute('data-upsell') === '1') count++;
      });
    });
    return count;
  }

  function cssEscape(s) {
    /* Very narrow CSS.escape polyfill , our values are plain ASCII
       (`plastic`, `3x`, etc.) so this is just defensive. */
    return String(s).replace(/"/g, '\\"');
  }

  function strongUpsell() {
    /* Strong = any one of these specific high-intent signals trips it.
       Question-key references must match data-question-key on each
       section in hematite-quiz.liquid. */
    var a = state.answers;
    if (a.wear_frequency === 'all-day') return true;
    if (a.gift_frequency === 'monthly' || a.gift_frequency === 'designated') return true;
    if (a.circle === 'family' || a.circle === 'multi') return true;
    if (a.supply_length === 'quarter' || a.supply_length === 'year') return true;
    if (a.commitment === 'very') return true;
    return false;
  }

  function recommendPack() {
    if (strongUpsell()) {
      return {
        key: 'longship',
        name: 'LONGSHIP · 5 BANDS · BUY 2 GET 3 FREE',
        label: 'YOUR STACK',
        reason: 'Five Hematite Bands. The right pick when you wear yours daily and have men in your life who would actually wear one. Covers a whole gift season: dad, brothers, mates, you. Free shipping, free replacement cord.',
        variantId: VARIANT_5PACK
      };
    }
    return {
      key: 'shieldwall',
      name: 'SHIELDWALL · 3 BANDS · BUY 2 GET 1 FREE',
      label: 'YOUR STACK',
      reason: 'Three Hematite Bands. The sweet-spot starter set: one for your wrist, one for your watch arm or sleep wrist, one to give. Free replacement cord and free shipping included.',
      variantId: VARIANT_3PACK
    };
  }

  var currentPack = null;

  function computeAndShowReveal() {
    var pack = recommendPack();
    currentPack = pack;
    var labelEl = root.querySelector('[data-quiz-reveal-label]');
    var nameEl = root.querySelector('[data-quiz-reveal-name]');
    var reasonEl = root.querySelector('[data-quiz-reveal-reason]');
    var ctaEl = root.querySelector('[data-quiz-reveal-cta]');
    if (labelEl) labelEl.textContent = pack.label;
    if (nameEl) nameEl.textContent = pack.name;
    if (reasonEl) reasonEl.textContent = pack.reason;
    if (ctaEl) {
      /* JS-disabled fallback: discount link with PDP redirect. With JS
         enabled, click handler intercepts and runs the add-to-cart +
         checkout flow below. */
      var redirect = PRODUCT_URL + '?quiz_pack=' + encodeURIComponent(pack.key);
      ctaEl.setAttribute('href', '/discount/' + DISCOUNT_CODE + '?redirect=' + encodeURIComponent(redirect));
    }
  }

  /* ---------- reveal CTA -> cart-add + checkout ----------
     POSTs the recommended variant to /cart/add.js, then navigates to
     /discount/IRON10?redirect=/checkout so the discount session is
     established and the customer lands on checkout with their set
     already in cart. Two requests but one user click. If anything
     fails, fall through to the href which routes to /discount + PDP. */
  function handleRevealClick(e) {
    var cta = e.target.closest('[data-quiz-reveal-cta]');
    if (!cta) return;
    if (!currentPack || !currentPack.variantId) return; /* falls back to href */
    e.preventDefault();

    /* Disable while in-flight so quick double-clicks don't double-add. */
    cta.setAttribute('aria-busy', 'true');
    cta.style.pointerEvents = 'none';

    var formData = new URLSearchParams();
    formData.set('id', currentPack.variantId);
    formData.set('quantity', '1');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData.toString(),
      credentials: 'same-origin'
    }).then(function (r) {
      /* Success or failure, route to discount-applied checkout. Shopify's
         /discount/CODE?redirect=/checkout endpoint applies the discount
         and forwards to /checkout immediately. If the add-to-cart failed,
         the checkout will just be empty , which is still a recoverable
         end state for the customer. */
      window.location.href = '/discount/' + encodeURIComponent(DISCOUNT_CODE)
        + '?redirect=' + encodeURIComponent('/checkout');
    }).catch(function () {
      /* Total network failure , fall back to the original href. */
      window.location.href = cta.getAttribute('href');
    });
  }

  /* ---------- interlude auto-advance ----------
     The interlude is a moment for the visitor to soak in the
     social-proof match graph; making them hunt for a Keep Going
     button kills that pacing. Instead we render a primary CTA with
     a gradient-fill child that slides from 0% to 100% over the
     screen's data-quiz-auto-advance duration (10s by default).
     When the fill completes, the JS auto-fires advance(). Clicking
     the button at any time short-circuits the timer. Pausing the
     timer when the tab is hidden avoids unexpected jumps. */
  var autoAdvanceTimer = null;
  var autoAdvanceRaf = null;
  var autoAdvanceStartedAt = 0;
  var autoAdvanceDuration = 0;
  var autoAdvanceFillEl = null;
  function startAutoAdvance(screen) {
    stopAutoAdvance();
    var btn = screen.querySelector('[data-quiz-auto-advance]');
    if (!btn) return;
    var duration = parseInt(btn.getAttribute('data-quiz-auto-advance'), 10);
    if (!duration || duration < 1) return;
    autoAdvanceFillEl = btn.querySelector('[data-quiz-fill]');
    if (!autoAdvanceFillEl) return;
    autoAdvanceFillEl.style.width = '0%';
    autoAdvanceStartedAt = performance.now();
    autoAdvanceDuration = duration;
    function tick(now) {
      var elapsed = now - autoAdvanceStartedAt;
      var pct = Math.min(100, (elapsed / autoAdvanceDuration) * 100);
      if (autoAdvanceFillEl) autoAdvanceFillEl.style.width = pct + '%';
      if (pct < 100 && autoAdvanceRaf !== null) {
        autoAdvanceRaf = requestAnimationFrame(tick);
      }
    }
    autoAdvanceRaf = requestAnimationFrame(tick);
    autoAdvanceTimer = setTimeout(function () { advance(); }, duration);
  }
  function stopAutoAdvance() {
    if (autoAdvanceTimer) { clearTimeout(autoAdvanceTimer); autoAdvanceTimer = null; }
    if (autoAdvanceRaf !== null) { cancelAnimationFrame(autoAdvanceRaf); autoAdvanceRaf = null; }
    if (autoAdvanceFillEl) { autoAdvanceFillEl.style.width = '0%'; autoAdvanceFillEl = null; }
  }
  document.addEventListener('visibilitychange', function () {
    /* If the user tabs away during the auto-advance window, kill the
       timer so they don't return to a screen later than expected.
       They'll see the static "Keep Going" CTA and can advance manually. */
    if (document.hidden && autoAdvanceTimer) stopAutoAdvance();
  });

  /* ---------- reveal product rotator ---------- */
  var rotatorTimer = null;
  function startRevealRotator() {
    stopRevealRotator();
    var imgs = root.querySelectorAll('[data-rotator-image]');
    if (!imgs.length) return;
    var current = 0;
    rotatorTimer = setInterval(function () {
      var next = (current + 1) % imgs.length;
      imgs[current].classList.remove('is-active');
      imgs[next].classList.add('is-active');
      current = next;
    }, 2400);
  }
  function stopRevealRotator() {
    if (rotatorTimer) {
      clearInterval(rotatorTimer);
      rotatorTimer = null;
    }
  }

  /* ---------- falling-hematite-band animation ---------- */
  function triggerFallingSticks(count) {
    var container = root.querySelector('[data-quiz-fall]');
    if (!container || !CORNER_ASSET) return;
    container.innerHTML = '';
    var i;
    for (i = 0; i < count; i++) {
      (function (n) {
        var stick = document.createElement('img');
        stick.src = CORNER_ASSET;
        stick.alt = '';
        stick.className = 'quiz-fall__stick';
        stick.style.left = (Math.random() * 100) + '%';
        stick.style.animationDelay = (Math.random() * 0.6) + 's';
        stick.style.animationDuration = (3.4 + Math.random() * 2.2) + 's';
        stick.style.setProperty('--fall-rot-start', (Math.random() * 120 - 60) + 'deg');
        stick.style.setProperty('--fall-rot-end',   (Math.random() * 540 - 270) + 'deg');
        stick.style.setProperty('--fall-scale',     (0.45 + Math.random() * 0.5).toFixed(2));
        container.appendChild(stick);
        /* Remove after the animation finishes so they don't accumulate
           in the DOM if the user re-triggers the screen. */
        stick.addEventListener('animationend', function () {
          if (stick.parentNode) stick.parentNode.removeChild(stick);
        });
      })(i);
    }
  }

  /* ---------- wiring ---------- */
  root.addEventListener('click', function (e) {
    handleRevealClick(e);
    handleAction(e);
    handleOptionClick(e);
  });

  /* On initial load, if user has saved state past intro, restore that screen.
     Otherwise show intro. */
  var initial = state.currentScreen && screenById[state.currentScreen]
    ? state.currentScreen
    : 'intro';
  showScreen(initial);

  /* For any multi-select question we resumed into, recreate the Continue
     button if there are existing selections. */
  Object.keys(screenById).forEach(function (sid) {
    var s = screenById[sid];
    if (s && s.getAttribute('data-question-multi') === 'true') {
      ensureMultiNext(s);
    }
  });
})();
